// ===========================================
// Save the Date Editor Page (Create / Edit)
// ===========================================
// Full-page form for creating or editing a Save the Date.
// Routes: save-the-dates/new  and  save-the-dates/:id

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Chip,
  FormControlLabel,
  Switch,
  Autocomplete,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Event as ConvertIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AppLocalizationProvider } from '../../components/common/AppLocalizationProvider';
import { format } from 'date-fns';
import PageHeader from '../../components/common/PageHeader';
import ChannelToggles from '../../components/events/ChannelToggles';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  useSaveTheDate,
  useCreateSaveTheDate,
  useUpdateSaveTheDate,
  useSendSaveTheDate,
  useScheduleSaveTheDate,
  useResendSaveTheDate,
  useAmendSaveTheDate,
  useConvertSaveTheDateToEvent,
} from '../../hooks/useSaveTheDates';
import type { SaveTheDateChannel } from '../../api/saveTheDates.api';
import { toLocalDateString, localDateFromKey } from '../../utils/dateUtils';
import type { EventVisibility } from '../../api/events.api';
import { useFamilies } from '../../hooks/useUsers';
import { useLessons } from '../../hooks/useLessons';
import { useAgeGroups, useProgramLevels } from '../../hooks/useAdmin';

// ===========================================
// VISIBILITY OPTIONS
// ===========================================

const visibilityOptions: { value: EventVisibility; label: string }[] = [
  { value: 'ALL_FAMILIES', label: 'All Families' },
  { value: 'SPECIFIC_FAMILIES', label: 'Specific Families' },
  { value: 'SPECIFIC_CLASSES', label: 'Specific Classes' },
  { value: 'SPECIFIC_AGE_GROUPS', label: 'Specific Age Groups' },
  { value: 'SPECIFIC_PROGRAM_LEVELS', label: 'Specific Program Levels' },
  { value: 'STAFF_ONLY', label: 'Staff Only' },
  { value: 'PUBLIC', label: 'Public' },
];

// ===========================================
// OPTION SHAPE
// ===========================================

interface TargetOption {
  id: string;
  label: string;
}

// ===========================================
// FORM STATE
// ===========================================

interface FormState {
  title: string;
  message: string;
  eventDate: Date | null;
  venueName: string;
  visibility: EventVisibility;
  channels: SaveTheDateChannel[];
  scheduledFor: Date | null;
}

const defaultForm: FormState = {
  title: '',
  message: '',
  eventDate: null,
  venueName: '',
  visibility: 'ALL_FAMILIES',
  channels: ['EMAIL'],
  scheduledFor: null,
};

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function SaveTheDateEditorPage() {
  const navigate = useNavigate();
  const { schoolSlug, id } = useParams<{ schoolSlug: string; id: string }>();
  const isEditing = !!id;

  const [form, setForm] = useState<FormState>(defaultForm);
  const [targetFamilies, setTargetFamilies] = useState<string[]>([]);
  const [targetClasses, setTargetClasses] = useState<string[]>([]);
  const [targetAgeGroups, setTargetAgeGroups] = useState<string[]>([]);
  const [targetProgramLevels, setTargetProgramLevels] = useState<string[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [showAmendDialog, setShowAmendDialog] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);

  // Load existing save the date (edit mode)
  const { data: std, isLoading: isLoadingStd } = useSaveTheDate(id ?? '');

  // Data for target pickers
  const { data: familiesData, isLoading: isLoadingFamilies } = useFamilies();
  const { data: lessonsData, isLoading: isLoadingLessons } = useLessons();
  const { data: ageGroupsData, isLoading: isLoadingAgeGroups } = useAgeGroups();
  const { data: programLevelsData, isLoading: isLoadingProgramLevels } = useProgramLevels();

  // Map to { id, label } option objects
  const familyOptions: TargetOption[] = (familiesData ?? []).map((f) => ({
    id: f.id,
    label: f.name,
  }));
  const classOptions: TargetOption[] = (lessonsData ?? []).map((l) => ({
    id: l.id,
    label: l.name,
  }));
  const ageGroupOptions: TargetOption[] = (ageGroupsData ?? []).map((ag) => ({
    id: ag.id,
    label: ag.name,
  }));
  const programLevelOptions: TargetOption[] = (programLevelsData ?? []).map((pl) => ({
    id: pl.id,
    label: pl.displayName ?? pl.name,
  }));

  // Populate form when editing
  useEffect(() => {
    if (std) {
      setForm({
        title: std.title,
        message: std.message ?? '',
        // Rebuild at LOCAL midnight from the stored calendar day. A plain `new Date()` on a
        // UTC-midnight `@db.Date` renders the PREVIOUS day west of UTC — and the picker would
        // then re-save that shifted day. The read must mirror the write (`toLocalDateString`).
        eventDate: std.eventDate ? localDateFromKey(std.eventDate) : null,
        venueName: std.venueName ?? '',
        visibility: std.visibility,
        channels: std.channels,
        // A genuine instant — parse as-is.
        scheduledFor: std.scheduledFor ? new Date(std.scheduledFor) : null,
      });
      setTargetFamilies(std.targetFamilies ?? []);
      setTargetClasses(std.targetClasses ?? []);
      setTargetAgeGroups(std.targetAgeGroups ?? []);
      setTargetProgramLevels(std.targetProgramLevels ?? []);
    }
  }, [std]);

  const createMutation = useCreateSaveTheDate();
  const updateMutation = useUpdateSaveTheDate();
  const sendMutation = useSendSaveTheDate();
  const scheduleMutation = useScheduleSaveTheDate();
  const resendMutation = useResendSaveTheDate();
  const amendMutation = useAmendSaveTheDate();
  const convertMutation = useConvertSaveTheDateToEvent();

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isSent = std?.status === 'SENT';
  // A SENT Save the Date is read-only by default. Turning on "amend mode" unlocks
  // the fields so the admin can edit date/time/location/etc. and resend.
  const [amendMode, setAmendMode] = useState(false);
  const readOnly = isSent && !amendMode;

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Derive selected options from id arrays
  const selectedFamilyOptions = familyOptions.filter((o) => targetFamilies.includes(o.id));
  const selectedClassOptions = classOptions.filter((o) => targetClasses.includes(o.id));
  const selectedAgeGroupOptions = ageGroupOptions.filter((o) => targetAgeGroups.includes(o.id));
  const selectedProgramLevelOptions = programLevelOptions.filter((o) =>
    targetProgramLevels.includes(o.id)
  );

  const validate = (): boolean => {
    if (!form.title.trim()) {
      setValidationError('Title is required.');
      return false;
    }
    if (form.visibility === 'SPECIFIC_FAMILIES' && targetFamilies.length === 0) {
      setValidationError('Please select at least one family.');
      return false;
    }
    if (form.visibility === 'SPECIFIC_CLASSES' && targetClasses.length === 0) {
      setValidationError('Please select at least one class.');
      return false;
    }
    if (form.visibility === 'SPECIFIC_AGE_GROUPS' && targetAgeGroups.length === 0) {
      setValidationError('Please select at least one age group.');
      return false;
    }
    if (form.visibility === 'SPECIFIC_PROGRAM_LEVELS' && targetProgramLevels.length === 0) {
      setValidationError('Please select at least one program level.');
      return false;
    }
    setValidationError(null);
    return true;
  };

  const buildPayload = () => ({
    title: form.title.trim(),
    message: form.message.trim() || null,
    // `eventDate` is a CALENDAR DAY (`@db.Date`), so it must travel as a bare `YYYY-MM-DD`
    // built from the admin's LOCAL day. `.toISOString()` here shifted a Sydney admin's
    // 20 Dec 09:00 to `2026-12-19T22:00Z`, which Postgres truncated to 19 Dec — and the wrong
    // day then went out in the Save the Date email AND its .ics attachment, to families.
    eventDate: form.eventDate ? toLocalDateString(form.eventDate) : null,
    venueName: form.venueName.trim() || null,
    visibility: form.visibility,
    channels: form.channels,
    // `scheduledFor` is a genuine INSTANT (when to send), not a calendar day — ISO is correct.
    scheduledFor: form.scheduledFor ? form.scheduledFor.toISOString() : null,
    targetFamilies,
    targetClasses,
    targetAgeGroups,
    targetProgramLevels,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = buildPayload();
    if (isEditing) {
      await updateMutation.mutateAsync({ id: id!, data: payload });
    } else {
      const result = await createMutation.mutateAsync(payload);
      navigate(`/${schoolSlug}/admin/save-the-dates/${result.id}`);
    }
  };

  const handleSendNow = async () => {
    if (!id) return;
    setShowSendDialog(false);
    await sendMutation.mutateAsync(id);
  };

  const handleSchedule = async () => {
    if (!id || !form.scheduledFor) return;
    await scheduleMutation.mutateAsync({
      id,
      scheduledFor: form.scheduledFor.toISOString(),
    });
  };

  // Plain resend — same content, unchanged.
  const handleResend = async () => {
    if (!id) return;
    setShowResendDialog(false);
    await resendMutation.mutateAsync(id);
  };

  // Amend — persist the edited fields, then resend as an amended STD.
  const handleAmend = async () => {
    if (!id) return;
    if (!validate()) {
      setShowAmendDialog(false);
      return;
    }
    setShowAmendDialog(false);
    await amendMutation.mutateAsync({ id, edits: buildPayload() });
    setAmendMode(false);
  };

  const handleConvert = async () => {
    if (!id) return;
    const result = await convertMutation.mutateAsync(id);
    navigate(`/${schoolSlug}/admin/events/${result.eventId}/edit`);
  };

  const handleBack = () => {
    navigate(`/${schoolSlug}/admin/save-the-dates`);
  };

  if (isEditing && isLoadingStd) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AppLocalizationProvider>
      <Box>
        <PageHeader
          title={isEditing ? 'Edit Save the Date' : 'New Save the Date'}
          subtitle={
            isEditing
              ? `Editing: ${std?.title ?? ''}`
              : 'Create an early announcement for an upcoming event'
          }
          breadcrumbs={[
            { label: 'Save the Dates', path: '/admin/save-the-dates' },
            { label: isEditing ? 'Edit' : 'New' },
          ]}
        />

        {/* Sent status banner + amend toggle */}
        {isSent && std && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Sent on {format(new Date(std.sentAt!), 'dd MMM yyyy, h:mm a')}
              {std.amendedCount > 0 && (
                <>
                  {' '}— amended {std.amendedCount} time{std.amendedCount !== 1 ? 's' : ''}
                  {std.lastSentAt && (
                    <> (last {format(new Date(std.lastSentAt), 'dd MMM yyyy, h:mm a')})</>
                  )}
                </>
              )}
            </Typography>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={amendMode}
                  onChange={(e) => setAmendMode(e.target.checked)}
                />
              }
              label="Amend this Save the Date (edit the details, then resend)"
            />
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {validationError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setValidationError(null)}>
              {validationError}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* Basic Details */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Basic Details
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Title"
                  value={form.title}
                  onChange={(e) => setField('title', e.target.value)}
                  required
                  fullWidth
                  InputProps={{ readOnly }}
                />
                <TextField
                  label="Message"
                  value={form.message}
                  onChange={(e) => setField('message', e.target.value)}
                  multiline
                  rows={4}
                  fullWidth
                  placeholder="Brief announcement message (optional)"
                  InputProps={{ readOnly }}
                />
                <TextField
                  label="Venue Name"
                  value={form.venueName}
                  onChange={(e) => setField('venueName', e.target.value)}
                  fullWidth
                  placeholder="e.g. School Hall (optional)"
                  InputProps={{ readOnly }}
                />
              </Stack>
            </Paper>

            {/* Audience */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Audience
              </Typography>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Visibility</InputLabel>
                  <Select
                    value={form.visibility}
                    label="Visibility"
                    onChange={(e) => setField('visibility', e.target.value as EventVisibility)}
                    inputProps={{ readOnly }}
                  >
                    {visibilityOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {form.visibility === 'SPECIFIC_FAMILIES' && (
                  <Box>
                    <Autocomplete
                      multiple
                      options={familyOptions}
                      value={selectedFamilyOptions}
                      loading={isLoadingFamilies}
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      readOnly={readOnly}
                      onChange={(_, selected) => setTargetFamilies(selected.map((o) => o.id))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select families"
                          placeholder={selectedFamilyOptions.length === 0 ? 'Search families...' : ''}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={option.label}
                            size="small"
                          />
                        ))
                      }
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Only the selected families will receive this Save the Date.
                    </Typography>
                  </Box>
                )}

                {form.visibility === 'SPECIFIC_CLASSES' && (
                  <Box>
                    <Autocomplete
                      multiple
                      options={classOptions}
                      value={selectedClassOptions}
                      loading={isLoadingLessons}
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      readOnly={readOnly}
                      onChange={(_, selected) => setTargetClasses(selected.map((o) => o.id))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select classes"
                          placeholder={selectedClassOptions.length === 0 ? 'Search classes...' : ''}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={option.label}
                            size="small"
                          />
                        ))
                      }
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Only students enrolled in the selected classes will receive this Save the Date.
                    </Typography>
                  </Box>
                )}

                {form.visibility === 'SPECIFIC_AGE_GROUPS' && (
                  <Box>
                    <Autocomplete
                      multiple
                      options={ageGroupOptions}
                      value={selectedAgeGroupOptions}
                      loading={isLoadingAgeGroups}
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      readOnly={readOnly}
                      onChange={(_, selected) => setTargetAgeGroups(selected.map((o) => o.id))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select age groups"
                          placeholder={selectedAgeGroupOptions.length === 0 ? 'Search age groups...' : ''}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={option.label}
                            size="small"
                          />
                        ))
                      }
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Only families with a student in the selected age groups will receive this Save the Date.
                    </Typography>
                  </Box>
                )}

                {form.visibility === 'SPECIFIC_PROGRAM_LEVELS' && (
                  <Box>
                    <Autocomplete
                      multiple
                      options={programLevelOptions}
                      value={selectedProgramLevelOptions}
                      loading={isLoadingProgramLevels}
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      readOnly={readOnly}
                      onChange={(_, selected) => setTargetProgramLevels(selected.map((o) => o.id))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select program levels"
                          placeholder={
                            selectedProgramLevelOptions.length === 0 ? 'Search program levels...' : ''
                          }
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={option.label}
                            size="small"
                          />
                        ))
                      }
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Only families with a student at the selected program levels will receive this Save the Date.
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Date & Scheduling */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Date &amp; Scheduling
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <DateTimePicker
                    label="Event Date (optional)"
                    value={form.eventDate}
                    onChange={(val) => setField('eventDate', val)}
                    readOnly={readOnly}
                    format="dd/MM/yyyy HH:mm"
                    slotProps={{
                      textField: {
                        fullWidth: false,
                        sx: { minWidth: 280 },
                        helperText: 'The date of the upcoming event you are announcing.',
                      },
                    }}
                  />
                </Box>
                <Divider />
                <Box>
                  <DateTimePicker
                    label="Schedule Send For (optional)"
                    value={form.scheduledFor}
                    onChange={(val) => setField('scheduledFor', val)}
                    readOnly={readOnly}
                    format="dd/MM/yyyy HH:mm"
                    slotProps={{
                      textField: {
                        fullWidth: false,
                        sx: { minWidth: 280 },
                        helperText: 'Leave blank to send manually using the "Send Now" button.',
                      },
                    }}
                  />
                </Box>
              </Stack>
            </Paper>

            {/* Delivery Channels */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Delivery Channels
              </Typography>
              <ChannelToggles
                value={form.channels}
                onChange={(channels) => setField('channels', channels)}
                disabled={readOnly}
              />
            </Paper>

            {/* Actions */}
            <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<BackIcon />}
                onClick={handleBack}
                disabled={isSaving}
              >
                {isSent ? 'Back' : 'Cancel'}
              </Button>

              {/* Convert to Event — shown when not yet linked to an event */}
              {isEditing && std?.eventId == null && (
                <Button
                  variant="outlined"
                  startIcon={
                    convertMutation.isPending ? <CircularProgress size={16} /> : <ConvertIcon />
                  }
                  onClick={handleConvert}
                  disabled={convertMutation.isPending}
                >
                  Convert to Event
                </Button>
              )}

              {/* Sent state: Resend (same content) is always available. When the
                  Amend toggle is on, a separate "Save & Resend" sends the edits. */}
              {isSent ? (
                <>
                  {amendMode ? (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={
                        amendMutation.isPending ? <CircularProgress size={16} /> : <SendIcon />
                      }
                      onClick={() => setShowAmendDialog(true)}
                      disabled={amendMutation.isPending}
                    >
                      Save &amp; Resend (Amended)
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={
                        resendMutation.isPending ? <CircularProgress size={16} /> : <SendIcon />
                      }
                      onClick={() => setShowResendDialog(true)}
                      disabled={resendMutation.isPending}
                    >
                      Resend
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {/* Save draft / update */}
                  <Button
                    type="submit"
                    variant="outlined"
                    startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                    disabled={isSaving}
                  >
                    {isEditing ? 'Save Changes' : 'Save Draft'}
                  </Button>

                  {/* Schedule — only available when editing and scheduledFor is set */}
                  {isEditing && form.scheduledFor && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={
                        scheduleMutation.isPending ? (
                          <CircularProgress size={16} />
                        ) : (
                          <ScheduleIcon />
                        )
                      }
                      onClick={handleSchedule}
                      disabled={scheduleMutation.isPending}
                    >
                      Schedule
                    </Button>
                  )}

                  {/* Send Now — only available when editing */}
                  {isEditing && (
                    <Button
                      variant="contained"
                      startIcon={
                        sendMutation.isPending ? <CircularProgress size={16} /> : <SendIcon />
                      }
                      onClick={() => setShowSendDialog(true)}
                      disabled={sendMutation.isPending}
                    >
                      Send Now
                    </Button>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </Box>

        {/* Send Now Confirm */}
        <ConfirmDialog
          open={showSendDialog}
          title="Send Save the Date"
          message="Send this Save the Date to all recipients now?"
          confirmLabel="Send"
          confirmColor="primary"
          loading={sendMutation.isPending}
          onConfirm={handleSendNow}
          onCancel={() => setShowSendDialog(false)}
        />

        {/* Resend Confirm (same content) */}
        <ConfirmDialog
          open={showResendDialog}
          title="Resend Save the Date"
          message="Re-send this Save the Date (unchanged) to all recipients?"
          confirmLabel="Resend"
          confirmColor="primary"
          loading={resendMutation.isPending}
          onConfirm={handleResend}
          onCancel={() => setShowResendDialog(false)}
        />

        {/* Amend & Resend Confirm */}
        <ConfirmDialog
          open={showAmendDialog}
          title="Save & Resend (Amended)"
          message="Save your changes and re-send this as an Amended Save the Date to all recipients?"
          confirmLabel="Save & Resend"
          confirmColor="primary"
          loading={amendMutation.isPending}
          onConfirm={handleAmend}
          onCancel={() => setShowAmendDialog(false)}
        />
      </Box>
    </AppLocalizationProvider>
  );
}
